import React, { useState, useEffect } from 'react';
import { DecodePackCombinations, PACKAGING_COSTS, SHIPPING_COST_PER_BOTTLE } from './DecodePackComb';

export const PackCombinationDisplay = ({ 
        item, 
        onSave, 
        onCombinationSelect,
        isExpanded = false,        // 🔥 Nuova prop
        onToggleExpand             // 🔥 Nuova prop
    }) => {

    const { quantita, id, prezzo_unitario, currentPacks } = item;
    const unitPrice = prezzo_unitario || 24.44;
    
    const [packQuantities, setPackQuantities] = useState({ 3: 0, 6: 0, 12: 0 });
    const [totalBottles, setTotalBottles] = useState(quantita || 0);
    const [isSaving, setIsSaving] = useState(false);
    
    // Inizializzazione
    useEffect(() => {
        if (currentPacks && Object.keys(currentPacks).length > 0) {
            setPackQuantities(currentPacks);
            const total = Object.entries(currentPacks).reduce((sum, [pack, count]) => sum + (parseInt(pack) * count), 0);
            setTotalBottles(total);
        } else if (quantita) {
            const result = DecodePackCombinations(quantita);
            if (result.packCounts) {
                setPackQuantities({
                    3: result.packCounts[3] || 0,
                    6: result.packCounts[6] || 0,
                    12: result.packCounts[12] || 0
                });
            }
        }
    }, [quantita, currentPacks]);
    
    // La funzione handleQuantityChange - per cambiare le quantità
    const handleQuantityChange = async (packSize, delta) => {
        if (isSaving) return;
        
        const newCount = Math.max(0, (packQuantities[packSize] || 0) + delta);
        const newQuantities = { ...packQuantities, [packSize]: newCount };
        const newTotal = Object.entries(newQuantities).reduce((sum, [size, count]) => sum + (parseInt(size) * count), 0);
        
        setPackQuantities(newQuantities);
        setTotalBottles(newTotal);
        
        setIsSaving(true);
        try {
            // 🔥 CHIAMA onSave SE ESISTE
            if (onSave) {
                await onSave(id, newQuantities, newTotal);
            }
            if (onCombinationSelect) {
                onCombinationSelect(id, { packQuantities: newQuantities, totalQuantity: newTotal });
            }
        } catch (error) {
            console.error('Errore salvataggio:', error);
        } finally {
            setIsSaving(false);
        }
    };
    
    const getPackPrice = (packSize, count) => unitPrice * packSize * count;
    const getPackShipping = (packSize, count) => packSize * SHIPPING_COST_PER_BOTTLE * count;
    const getPackPackaging = (packSize, count) => PACKAGING_COSTS[packSize] * count;
    
    const calculateTotals = () => {
        let totalPrice = 0, totalShipping = 0, totalPackaging = 0, totalBottlesCount = 0;
        Object.entries(packQuantities).forEach(([packSize, count]) => {
            if (count === 0) return;
            const size = parseInt(packSize);
            totalPrice += unitPrice * size * count;
            totalShipping += size * SHIPPING_COST_PER_BOTTLE * count;
            totalPackaging += PACKAGING_COSTS[size] * count;
            totalBottlesCount += size * count;
        });
        return { totalPrice, totalShipping, totalPackaging, totalBottles: totalBottlesCount, grandTotal: totalPrice + totalShipping + totalPackaging };
    };
    
    const totals = calculateTotals();
    const activePacks = Object.entries(packQuantities).filter(([_, count]) => count > 0);
    
    if (activePacks.length === 0) {
        return (
            <div className="pack-empty">
                <span className="pack-empty-icon">📦</span>
                <span className="pack-empty-text">Nessuna confezione</span>
                <button className="pack-add-btn" onClick={() => handleQuantityChange(3, 1)} disabled={isSaving}>+ Aggiungi</button>
            </div>
        );
    }
    
    return (
        <div className="pack-compact">
            <div className="pack-compact-header"  onClick={() => onToggleExpand && onToggleExpand()}>
                <div className="pack-compact-summary">
                    <span className="pack-icon">📦</span>
                    <span className="pack-summary-text">
                        {activePacks.map(([size, count]) => `${count}×${size}`).join(' + ')}
                    </span>
                    <span className="pack-summary-total">({totalBottles} bottiglie)</span>
                </div>
                <div className="pack-compact-right">
                    {!isExpanded && <span className="pack-summary-price">€{totals.grandTotal.toFixed(2)}</span>}
                    <span className="pack-toggle-icon">{isExpanded ? '▲' : '▼'}</span>
                </div>
            </div>
            
            {isExpanded && (
                <div className="pack-compact-details">
                    {activePacks.map(([packSize, count]) => {
                        const size = parseInt(packSize);
                        const itemTotal = getPackPrice(size, count) + getPackShipping(size, count) + getPackPackaging(size, count);
                        return (
                            <div key={packSize} className="pack-compact-item">
                                <div className="pack-compact-item-info">
                                    <span className="pack-compact-badge">📦 {size} Unità</span>
                                    <div className="pack-compact-controls">
                                        <button className="qty-btn-mini" onClick={() => handleQuantityChange(size, -1)} disabled={isSaving}>−</button>
                                        <span className="qty-value-mini">{count}</span>
                                        <button className="qty-btn-mini" onClick={() => handleQuantityChange(size, 1)} disabled={isSaving}>+</button>
                                        <button className="qty-btn-remove" onClick={() => handleQuantityChange(size, -count)} disabled={isSaving} title="Rimuovi">✕</button>
                                    </div>
                                </div>
                                <div className="pack-compact-item-price">€{itemTotal.toFixed(2)}</div>
                            </div>
                        );
                    })}
                    
                    <div className="pack-compact-add">
                        <span className="add-label"><strong>+</strong> Aggiungi:</span>
                        <button className="add-pack-mini" onClick={() => handleQuantityChange(3, 1)} disabled={isSaving}>3</button>
                        <button className="add-pack-mini" onClick={() => handleQuantityChange(6, 1)} disabled={isSaving}>6</button>
                        <button className="add-pack-mini" onClick={() => handleQuantityChange(12, 1)} disabled={isSaving}>12</button>
                    </div>
                    
                    <div className="pack-compact-total">
                        <div className="pack-total-line">
                            <span>Prodotti</span>
                            <span>€{totals.totalPrice.toFixed(2)}</span>
                        </div>
                        <div className="pack-total-line">
                            <span>Spedizione</span>
                            <span>€{totals.totalShipping.toFixed(2)}</span>
                        </div>
                        <div className="pack-total-line">
                            <span>Imballaggio</span>
                            <span>€{totals.totalPackaging.toFixed(2)}</span>
                        </div>
                        <div className="pack-total-line grand">
                            <span>Totale</span>
                            <span>€{totals.grandTotal.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            )}
            {isSaving && <div className="pack-saving-indicator">💾 Salvataggio...</div>}
        </div>
    );
};
